import { Alerta, Badge, Button, DataList, Dialog, MetricCard, Row, Select, Spinner, Stack } from "@/components";
import type { Unidad } from "@/lib/api";
import { formatMoneda } from "@/lib/format";
import { detalleResumen, etiquetaPiso, formatM2, formatPorcentajeUnidad, nombreUnidad, OPCIONES_FILTRO_TIPO, OPCIONES_FILTRO_VENTA, type FiltroVenta } from "@/lib/unidades";
import { TONO_ESTADO_UNIDAD, etiquetaEstado } from "@/lib/ventas";
import { FormularioUnidad } from "./FormularioUnidad";
import { useUnidadesTab } from "./useUnidadesTab";

export function UnidadesTab() {
  const t = useUnidadesTab();

  const columnas = [
    {
      clave: "unidad",
      titulo: "Unidad",
      render: (u: Unidad) => (
        <Row espacio={2}>
          <span>{nombreUnidad(u)}</span>
          <Badge tono={TONO_ESTADO_UNIDAD[u.estado_venta]}>{etiquetaEstado(u)}</Badge>
        </Row>
      ),
    },
    { clave: "piso", titulo: "Piso", render: (u: Unidad) => etiquetaPiso(u.piso) },
    { clave: "m2", titulo: "Superficie", render: (u: Unidad) => formatM2(u.sup_total_m2) },
    { clave: "m2_homologados", titulo: "m² homologados", render: (u: Unidad) => formatM2(u.m2_homologados) },
    { clave: "porcentaje", titulo: "% fideicomiso", render: (u: Unidad) => (u.porcentaje_fideicomiso === null ? "—" : formatPorcentajeUnidad(u.porcentaje_fideicomiso)) },
    ...(t.haySugerido
      ? [{ clave: "sugerido", titulo: "Precio sugerido", render: (u: Unidad) => (u.precio_sugerido_usd === null ? "—" : formatMoneda(u.precio_sugerido_usd, "USD")) }]
      : []),
  ];

  const acciones = (u: Unidad) => (
    <>
      <Button variante="secundario" tamanio="sm" aria-label={`Ver ${nombreUnidad(u)}`} onClick={() => t.verFicha(u.id)}>
        Ver
      </Button>
      {t.esAdmin && (
        <Button variante="fantasma" tamanio="sm" aria-label={`Duplicar ${nombreUnidad(u)}`} onClick={() => t.duplicar(u)}>
          Duplicar
        </Button>
      )}
    </>
  );

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Unidades</h1>
        {t.esAdmin && (
          <Row espacio={2}>
            <Button variante="secundario" onClick={t.verPonderacion}>
              Ponderación
            </Button>
            {t.haySugerido && (
              <Button variante="secundario" onClick={t.abrirUsar}>
                Usar precio sugerido
              </Button>
            )}
            <Button onClick={t.abrirNuevo}>Nueva unidad</Button>
          </Row>
        )}
      </Row>

      {t.resumen && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard titulo="Unidades" valor={String(t.resumen.total.cantidad)} detalle={detalleResumen(t.resumen.total)} />
          <MetricCard titulo="Superficie total" valor={formatM2(t.resumen.total.sup_total_m2)} detalle="Cubierta + semicubierta + descubierta" />
          {t.resumen.total.cantidad > 0 && (
            <MetricCard titulo="m² homologados" valor={formatM2(t.resumen.m2_en_venta)} detalle="El 100 % del fideicomiso (sin las fuera de venta)" />
          )}
          {t.resumen.m2_reservados_clase_b !== "0.00" && (
            <MetricCard
              titulo="Disponibles para vender"
              valor={formatM2(t.resumen.m2_disponibles_para_vender)}
              detalle={`${formatM2(t.resumen.m2_en_venta)} homologados − ${formatM2(t.resumen.m2_reservados_clase_b)} de fiduciantes clase B`}
            />
          )}
        </div>
      )}

      {t.usadas !== null && (
        <p role="status" className="rounded-control bg-exito-suave px-3 py-2 text-chico text-exito">
          {t.usadas === 1 ? "Se actualizó el precio de lista de 1 unidad" : `Se actualizó el precio de lista de ${t.usadas} unidades`}
        </p>
      )}

      <Row envolver espacio={3} alinear="fin">
        <Select etiqueta="Tipo" opciones={OPCIONES_FILTRO_TIPO} valor={t.filtros.tipo} onCambio={(v) => t.cambiarFiltro("tipo", v)} className="min-w-44" />
        <Select etiqueta="Venta" opciones={OPCIONES_FILTRO_VENTA} valor={t.filtros.venta} onCambio={(v) => t.cambiarFiltro("venta", v as FiltroVenta)} className="min-w-40" />
        {t.hayFiltros && (
          <Button variante="fantasma" onClick={t.limpiarFiltros}>
            Limpiar filtros
          </Button>
        )}
      </Row>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {!t.cargando && !t.errorCarga && (
        <DataList etiqueta="Unidades" columnas={columnas} filas={t.items} claveFila={(u) => u.id} acciones={acciones} vacio="Todavía no hay unidades cargadas." />
      )}

      <Dialog abierto={t.abierto} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Nueva unidad">
        <FormularioUnidad form={t.form} cambiar={t.cambiar} errores={t.errores} total={t.total} onSubmit={(e) => void t.guardar(e)} onCancelar={t.cerrar} guardando={t.guardando} error={t.errorGuardar} />
      </Dialog>

      <Dialog abierto={t.usarAbierto} onCambio={(abierto) => !abierto && t.cerrarUsar()} titulo="Usar el precio sugerido">
        <Stack espacio={4}>
          <p>
            {t.disponiblesConSugerido === 1
              ? "El precio sugerido pasa a ser el precio de lista de la unidad disponible."
              : `El precio sugerido pasa a ser el precio de lista de las ${t.disponiblesConSugerido} unidades disponibles.`}{" "}
            Las reservadas, vendidas, adjudicadas y fuera de venta no se tocan. El precio de lista es una referencia: el de cada venta se fija en la venta.
          </p>
          <Alerta texto={t.errorUsar} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cerrarUsar}>
              Cancelar
            </Button>
            <Button cargando={t.usando} onClick={() => void t.confirmarUsar()}>
              Usar como precio de lista
            </Button>
          </Row>
        </Stack>
      </Dialog>
    </Stack>
  );
}
